import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Truck,
  Globe,
  Headphones,
  Mail,
  Phone,
  Search,
  CheckCircle,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  LogIn
} from "lucide-react";
import LogoIndak from "../../assets/img/logo-pdf.png"; // Transparent wide logo
import ImageCargoPlaneAI from "../../assets/img/cargo_plane_ai.png"; // AI Cargo image
import airplaneVideo from "../../assets/videos/airplane.mp4";
import { getAutoTheme } from "../../utils/themeHelper";
import "./home.css";

export default function Home() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getAutoTheme);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  // Tracking form state
  const [jenisTracking, setJenisTracking] = useState("01");
  const [noResi, setNoResi] = useState("");

  // Contact form state
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    // Sync language attribute on document html
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;

    // Scroll Reveal Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [lang]);

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("landing-theme", newTheme);
  };

  const handleTrackingSubmit = (e) => {
    e.preventDefault();
    if (!noResi.trim()) {
      toast.error(lang === "ar" ? "يرجى إدخال رقم التتبع أولاً" : "Please enter a tracking number");
      return;
    }
    navigate(`/tracking/${jenisTracking}/${noResi.trim()}`);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) {
      toast.error(lang === "ar" ? "يرجى ملء جميع الحقول" : "Please fill in all fields");
      return;
    }
    setFormSubmitted(true);
    toast.success(lang === "ar" ? "جاري توجيهك إلى واتساب والبريد..." : "Redirecting you to WhatsApp and Mail...");

    const fullMsg = `Name: ${contactName}\nEmail: ${contactEmail}\nMessage: ${contactMessage}`;

    window.open(`https://wa.me/6287773243055?text=${encodeURIComponent(fullMsg)}`, "_blank");
    window.location.href = `mailto:info@indak.id?subject=Contact Form - ${encodeURIComponent(contactName)}&body=${encodeURIComponent(fullMsg)}`;

    setContactName("");
    setContactEmail("");
    setContactMessage("");
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setLangOpen(false);
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const text = {
    en: {
      navHome: "Home",
      navAbout: "About",
      navServices: "Services",
      navContact: "Contact",
      navCheckRates: "Check Rates",
      navSignIn: "Sign In",
      navDashboard: "Dashboard",
      heroTitle: "Reliable Delivery Across Borders",
      heroSubtitle: "From Indonesia to Yemen and Saudi Arabia, we handle your goods with care.",
      btnGetStarted: "Get Started",
      btnTrackShipment: "Lacak Resi",
      trackTitle: "🚀 Track Your Shipment",
      trackDesc: "Enter your type and tracking number to monitor live status.",
      trackService: "Service Type",
      trackCargo: "📦 Cargo Shipping",
      trackRemitOut: "💵 Money Transfer (Outbound)",
      trackRemitIn: "🇮🇩 Money Transfer (to Indonesia)",
      trackPlaceholder: "Enter your tracking number...",
      trackBtnSubmit: "Track Now",
      aboutBadge: "About Us",
      aboutTitle: "Connecting Indonesia Globally",
      aboutDesc: "At INDAK, we specialize in shipping goods and cash transfer services from Indonesia to the Middle East, with a primary focus on Yemen and Saudi Arabia. We are committed to bridging distances by providing fast, secure, and efficient logistical solutions.",
      aboutStat1: "Served Destinations",
      aboutStat2: "International Partners",
      aboutStat3: "Customer Support",
      servicesBadge: "Our Services",
      servicesTitle: "Best Shipping Solutions",
      servicesDesc: "Remittance and cargo solutions customized to support your import/remit needs safely.",
      service1Title: "Fast Shipping",
      service1Desc: "Fast air and sea cargo shipments handled professionally at every stage of transit.",
      service2Title: "Global Reach",
      service2Desc: "Wide network connecting Indonesia directly with Yemen, Saudi Arabia, and beyond.",
      service3Title: "Customer Care",
      service3Desc: "Responsive support team available to help monitor and resolve issues for all your packages.",
      contactBadge: "Contact",
      contactTitle: "Get In Touch",
      contactDesc: "Have questions about rates, delivery times, or want to consult? Contact us directly through the details below.",
      contactEmail: "Official Email",
      contactPhone: "Phone / WhatsApp",
      contactArea: "Service Coverage",
      contactFormTitle: "Send Message",
      formName: "Full Name",
      formEmail: "Email Address",
      formMessage: "Message",
      formPlaceholderName: "Your Name...",
      formPlaceholderMessage: "Write your message here...",
      formBtnSubmit: "Send Message",
      formBtnSuccess: "Sent",
      footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
      footerCopy: "All rights reserved."
    },
    ar: {
      navHome: "الرئيسية",
      navAbout: "من نحن",
      navServices: "خدماتنا",
      navContact: "اتصل بنا",
      navCheckRates: "أسعار الشحن",
      navSignIn: "تسجيل الدخول",
      navDashboard: "لوحة التحكم",
      heroTitle: "شحن موثوق عبر الحدود",
      heroSubtitle: "من إندونيسيا إلى اليمن والمملكة العربية السعودية، نقوم بنقل بضائعكم وحوالاتكم المالية بأقصى درجات العناية.",
      btnGetStarted: "ابدأ الآن",
      btnTrackShipment: "تتبع الشحنات",
      trackTitle: "🚀 تتبع شحنتك",
      trackDesc: "أدخل نوع الخدمة ورقم التتبع لمراقبة حالة شحنتك مباشرة.",
      trackService: "نوع الخدمة",
      trackCargo: "📦 شحن البضائع",
      trackRemitOut: "💵 إرسال الأموال (إلى الخارج)",
      trackRemitIn: "🇮🇩 إرسال الأموال (إلى إندونيسيا)",
      trackPlaceholder: "أدخل رقم التتبع هنا...",
      trackBtnSubmit: "تتبع الآن",
      aboutBadge: "من نحن",
      aboutTitle: "ربط إندونيسيا بالعالم",
      aboutDesc: "في إنداك، نحن متخصصون في تقديم خدمات شحن البضائع والتحويلات المالية من إندونيسيا إلى الشرق الأوسط، مع التركيز بشكل خاص على اليمن والمملكة العربية السعودية. نحن ملتزمون بتسهيل اللوجستيات وتوفير حلول آمنة وسريعة وفعالة.",
      aboutStat1: "وجهة مغطاة",
      aboutStat2: "شريك دولي",
      aboutStat3: "دعم العملاء",
      servicesBadge: "خدماتنا",
      servicesTitle: "أفضل حلول الشحن",
      servicesDesc: "حلول الشحن والحوالات المالية المصممة خصيصاً لدعم احتياجات الاستيراد والتحويل بأمان.",
      service1Title: "شحن سريع",
      service1Desc: "شحنات جوية وبحرية سريعة يتم التعامل معها باحترافية في كل مرحلة من مراحل النقل.",
      service2Title: "تغطية عالمية",
      service2Desc: "شبكة واسعة تربط إندونيسيا مباشرة باليمن والمملكة العربية السعودية وخارجها.",
      service3Title: "خدمة العملاء",
      service3Desc: "فريق دعم عملاء متجاوب لمساعدتك في متابعة شحناتك والإجابة على أي استفسار.",
      contactBadge: "اتصل بنا",
      contactTitle: "تواصل معنا",
      contactDesc: "لديك أسئلة حول الأسعار، أوقات التسليم، أو ترغب في استشارة؟ تواصل معنا مباشرة عبر التفاصيل أدناه.",
      contactEmail: "البريد الإلكتروني",
      contactPhone: "الهاتف / واتساب",
      contactArea: "مناطق التغطية",
      contactFormTitle: "إرسال رسالة",
      formName: "الاسم الكامل",
      formEmail: "البريد الإلكتروني",
      formMessage: "الرسالة",
      formPlaceholderName: "اسمك الكريم...",
      formPlaceholderMessage: "اكتب رسالتك أو استفسارك هنا...",
      formBtnSubmit: "إرسال الرسالة",
      formBtnSuccess: "تم الإرسال",
      footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
      footerCopy: "جميع الحقوق محفوظة."
    },
    id: {
      navHome: "Beranda",
      navAbout: "Tentang Kami",
      navServices: "Layanan",
      navContact: "Kontak",
      navCheckRates: "Cek Ongkir",
      navSignIn: "Masuk",
      navDashboard: "Dashboard",
      heroTitle: "Pengiriman Terpercaya Lintas Negara",
      heroSubtitle: "Dari Indonesia ke Yaman dan Arab Saudi, kami menangani barang & pengiriman Anda dengan sepenuh hati.",
      btnGetStarted: "Mulai Sekarang",
      btnTrackShipment: "Lacak Resi",
      trackTitle: "🚀 Lacak Pengiriman Anda",
      trackDesc: "Masukkan jenis layanan dan nomor resi untuk memantau status secara langsung.",
      trackService: "Jenis Layanan",
      trackCargo: "📦 Pengiriman Kargo",
      trackRemitOut: "💵 Transfer Uang (Outbound)",
      trackRemitIn: "🇮🇩 Transfer Uang (ke Indonesia)",
      trackPlaceholder: "Masukkan nomor resi...",
      trackBtnSubmit: "Lacak Sekarang",
      aboutBadge: "Tentang Kami",
      aboutTitle: "Menghubungkan Indonesia Secara Global",
      aboutDesc: "Di INDAK, kami mengkhususkan diri dalam pengiriman barang dan pengiriman uang dari Indonesia ke Timur Tengah, terutama Yaman dan Arab Saudi. Kami berkomitmen untuk menjembatani jarak dengan solusi logistik yang cepat, aman, dan efisien.",
      aboutStat1: "Destinasi Terjangkau",
      aboutStat2: "Mitra Internasional",
      aboutStat3: "Layanan Pelanggan",
      servicesBadge: "Layanan Kami",
      servicesTitle: "Solusi Pengiriman Terbaik",
      servicesDesc: "Layanan kargo dan transfer uang yang disesuaikan untuk mendukung kebutuhan impor/remitansi Anda secara aman.",
      service1Title: "Pengiriman Cepat",
      service1Desc: "Pengiriman kargo udara dan laut yang ditangani secara profesional di setiap tahap transit.",
      service2Title: "Jangkauan Global",
      service2Desc: "Jaringan luas yang menghubungkan Indonesia langsung dengan Yaman, Arab Saudi, dan sekitarnya.",
      service3Title: "Layanan Pelanggan",
      service3Desc: "Tim dukungan responsif siap membantu memantau dan menyelesaikan pertanyaan untuk semua paket Anda.",
      contactBadge: "Kontak",
      contactTitle: "Hubungi Kami",
      contactDesc: "Ada pertanyaan tentang tarif, waktu pengiriman, atau ingin berkonsultasi? Hubungi kami langsung melalui detail di bawah ini.",
      contactEmail: "Email Resmi",
      contactPhone: "Telepon / WhatsApp",
      contactArea: "Cakupan Layanan",
      contactFormTitle: "Kirim Pesan",
      formName: "Nama Lengkap",
      formEmail: "Alamat Email",
      formMessage: "Pesan",
      formPlaceholderName: "Nama Anda...",
      formPlaceholderMessage: "Tulis pesan Anda di sini...",
      formBtnSubmit: "Kirim Pesan",
      formBtnSuccess: "Terkirim",
      footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
      footerCopy: "Hak cipta dilindungi undang-undang."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* HEADER NAVBAR */}
      <header className="landing-header">
        <div className="header-container">
          <div className="header-logo" onClick={() => scrollToSection("hero")}>
            <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
          </div>

          <nav className="desktop-nav">
            <button onClick={() => scrollToSection("hero")} className="nav-link active-link-highlight">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="nav-link">{t.navCheckRates}</button>

            {/* Language Switcher */}
            <div className="lang-dropdown-container">
              <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                <ChevronDown size={14} />
              </button>
              {langOpen && (
                <div className="lang-dropdown-menu">
                  <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                    English
                  </button>
                  <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                    Bahasa Indonesia
                  </button>
                  <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                    العربية
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {/* Login Door Icon Button */}
            <button
              onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")}
              className="nav-sig-button"
              title={isLoggedIn ? t.navDashboard : t.navSignIn}
            >
              <LogIn size={20} />
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => scrollToSection("hero")} className="mobile-nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>

            <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => changeLanguage("en")}
                  style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  EN
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("id")}
                  style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  ID
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("ar")}
                  style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  AR
                </button>
              </div>

              <button
                onClick={toggleTheme}
                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>

            <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
              <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
            </button>
          </div>
        )}
      </header>

      <section id="hero" className="hero-section">
        <div className="hero-video-bg">
          <video 
            src={airplaneVideo} 
            autoPlay 
            loop 
            muted={true} 
            defaultMuted={true} 
            playsInline 
            className="hero-video"
          />
          <div className="hero-video-overlay"></div>
        </div>

        <div className="hero-center-content">
          <h1 className="hero-title animate-fade-in-up">
            {lang === "ar" ? (
              <>
                شحن موثوق <span className="text-gradient">عبر الحدود</span>
              </>
            ) : (
              <>
                Reliable Delivery <br />
                <span className="text-gradient">Across Borders</span>
              </>
            )}
          </h1>
          <p className="hero-subtitle animate-fade-in-up-delay-1">
            {t.heroSubtitle}
          </p>
          <div className="hero-actions animate-fade-in-up-delay-2">
            <button onClick={() => scrollToSection("tracking-widget")} className="btn-primary-landing">
              {t.btnGetStarted}
            </button>
            {/* <button onClick={() => scrollToSection("tracking-widget")} className="btn-secondary-landing">
              {t.btnTrackShipment}
            </button> */}
          </div>
        </div>

        {/* THREE bottom image cards from Pexels matching live site layout */}
        <div className="hero-bottom-cards animate-fade-in-up-delay-3">
          <div className="hero-card-img-box">
            <img src="https://images.pexels.com/photos/1638424/pexels-photo-1638424.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Logistics delivery truck" />
          </div>
          <div className="hero-card-img-box">
            <img src="https://images.pexels.com/photos/2026324/pexels-photo-2026324.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Airport Runway Cargo Planes" />
          </div>
          <div className="hero-card-img-box">
            <img src="https://images.pexels.com/photos/4392276/pexels-photo-4392276.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Courier courier package delivery" />
          </div>
        </div>
      </section>

      {/* DEDICATED TRACKING WIDGET SECTION */}
      <section id="tracking-widget" className="services-section reveal-scale" style={{ padding: "80px 0" }}>
        <div className="section-container">
          <div className="tracking-card-large">
            <div className="card-header" style={{ marginBottom: 24, textAlign: "center" }}>
              <h3>{t.trackTitle}</h3>
              <p>{t.trackDesc}</p>
            </div>
            <form onSubmit={handleTrackingSubmit} className="tracking-form">
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label htmlFor="jenisTracking">{t.trackService}</label>
                <select
                  id="jenisTracking"
                  value={jenisTracking}
                  onChange={(e) => setJenisTracking(e.target.value)}
                  className="select-custom"
                  style={{ width: "100%", padding: "12px", background: "var(--input-bg)", color: "var(--text-main)", border: "1px solid var(--border-color)", borderRadius: 8 }}
                >
                  <option value="01">{t.trackCargo}</option>
                  <option value="02">{t.trackRemitOut}</option>
                  <option value="03">{t.trackRemitIn}</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 24 }}>
                <label htmlFor="noResi">{t.formName === "الاسم الكامل" ? "رقم التتبع / المعاملة" : "Tracking / Transaction Number"}</label>
                <div className="input-with-icon">
                  <Search className="input-icon" size={18} />
                  <input
                    type="text"
                    id="noResi"
                    placeholder={t.trackPlaceholder}
                    value={noResi}
                    onChange={(e) => setNoResi(e.target.value)}
                    className="input-custom"
                    style={{ width: "100%", padding: "12px 12px 12px 40px", background: "var(--input-bg)", color: "var(--text-main)", border: "1px solid var(--border-color)", borderRadius: 8 }}
                  />
                </div>
              </div>
              <button type="submit" className="btn-track-submit" style={{ width: "100%" }}>
                {t.trackBtnSubmit}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* STATS & ABOUT SECTION (With Motion Reveal) */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-image-container reveal-left">
              <img src={ImageCargoPlaneAI} alt="Indak Courier Services AI Cargo" className="about-img" />
              <div className="floating-stat-card">
                <span className="stat-number">100%</span>
                <span className="stat-desc">{lang === "ar" ? "آمن وموثوق" : "Safe & Secure"}</span>
              </div>
            </div>
            <div className="about-content reveal-right">
              <span className="section-badge">{t.aboutBadge}</span>
              <h2 className="section-title">{t.aboutTitle}</h2>
              <p className="section-desc">
                {t.aboutDesc}
              </p>

              <div className="stats-row">
                <div className="stat-item">
                  <h3>150+</h3>
                  <p>{t.aboutStat1}</p>
                </div>
                <div className="stat-item">
                  <h3>15+</h3>
                  <p>{t.aboutStat2}</p>
                </div>
                <div className="stat-item">
                  <h3>24/7</h3>
                  <p>{t.aboutStat3}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION (With Scale motion reveals) */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header-center reveal">
            <span className="section-badge">{t.servicesBadge}</span>
            <h2 className="section-title">{t.servicesTitle}</h2>
            <p className="section-desc-center">
              {t.servicesDesc}
            </p>
          </div>

          <div className="services-grid">
            {/* Service 1 */}
            <div className="service-card reveal-scale reveal-delay-1">
              <div className="service-icon-wrapper">
                <Truck className="service-icon" size={28} />
              </div>
              <h3>{t.service1Title}</h3>
              <p>{t.service1Desc}</p>
            </div>

            {/* Service 2 */}
            <div className="service-card reveal-scale reveal-delay-2">
              <div className="service-icon-wrapper">
                <Globe className="service-icon" size={28} />
              </div>
              <h3>{t.service2Title}</h3>
              <p>{t.service2Desc}</p>
            </div>

            {/* Service 3 */}
            <div className="service-card reveal-scale reveal-delay-3">
              <div className="service-icon-wrapper">
                <Headphones className="service-icon" size={28} />
              </div>
              <h3>{t.service3Title}</h3>
              <p>{t.service3Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION (With Split Motion Reveals) */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            <div className="contact-info reveal-left">
              <span className="section-badge">{t.contactBadge}</span>
              <h2 className="section-title">{t.contactTitle}</h2>
              <p className="contact-intro">
                {t.contactDesc}
              </p>

              <div className="contact-details-list">
                <div className="contact-detail-item">
                  <div className="contact-icon-box">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h4>{t.contactEmail}</h4>
                    <p>info@indak.id</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon-box">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h4>{t.contactPhone}</h4>
                    <p>+62 877-7324-3055</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon-box">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h4>{t.contactArea}</h4>
                    <p>{lang === "ar" ? "إندونيسيا - اليمن - السعودية (الشرق الأوسط)" : "Indonesia - Yemen - Saudi Arabia (Middle East)"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container reveal-right">
              <h3 className="form-card-title">{t.contactFormTitle}</h3>
              <form onSubmit={handleContactSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="contactName">{t.formName}</label>
                  <input
                    type="text"
                    id="contactName"
                    placeholder={t.formPlaceholderName}
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="input-custom-dark"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contactEmail">{t.formEmail}</label>
                  <input
                    type="email"
                    id="contactEmail"
                    placeholder="name@email.com..."
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="input-custom-dark"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contactMessage">{t.formMessage}</label>
                  <textarea
                    id="contactMessage"
                    rows="4"
                    placeholder={t.formPlaceholderMessage}
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    className="textarea-custom-dark"
                  ></textarea>
                </div>
                <button type="submit" className="btn-contact-submit" disabled={formSubmitted}>
                  {formSubmitted ? (
                    <span className="submit-success"><CheckCircle size={16} /> {t.formBtnSuccess}</span>
                  ) : (
                    t.formBtnSubmit
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main-grid">
            <div className="footer-col footer-col-brand">
              <p className="footer-brand-desc">{t.footerDesc}</p>
            </div>

            <div className="footer-col footer-col-links">
              <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
              <ul className="footer-links-list">
                <li><button onClick={() => scrollToSection("hero")}>{t.navHome}</button></li>
                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
              </ul>
            </div>

            <div className="footer-col footer-col-services">
              <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
              <ul className="footer-links-list text-muted-list">
                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                <li>📦 Air & Sea Cargo Shipping</li>
                <li>💵 Outbound & Inbound Money Transfer</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
            </p>
            {/* <div className="dev-tag">
              <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
              <span className="dev-name">~ YallaBob ~</span>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
}
